import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ADMIN_ROLE_SLUG, OPERATIVE_ROLE_SLUG } from '~/Constants'
import DairyQuery from '~/QueryHooks/DairyQuery'
import Layout from '~/components/Layout'
import { useAuth } from '~/routes/components/AuthProvider'
import { El as LayoutEl } from '~/components/Layout/style';
import notify from '~/components/Notify'
import Dialog from '~/components/Dialog'
import { Icon, Label, Popup, Table } from 'semantic-ui-react'
import CustomIcon from '~/components/CustomIcon'
import CreateEditRestrictionPeriods from './CreateEditRestrictionPeriods'
import styled from 'styled-components'

const StyledTable = styled(Table)`
  &&& {
    border: 1px solid rgba(34, 36, 38, 0.15);
    border-radius: 4px;
  }
`

const ExpandableRow = styled(Table.Row)`
  cursor: pointer;
  &:hover {
    background-color: #f9f9f9 !important;
  }
`

const ExpandedContent = styled.div`
  padding: 20px;
  background-color: #f8f9fa;
  border-top: 1px solid #e0e0e0;
`

const DayDetailContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  background-color: white;
  border-radius: 4px;
  margin-bottom: 10px;
  border: 1px solid #e0e0e0;
`

const DayDetailSection = styled.div`
  display: flex;
  align-items: center;
  margin-right: 40px;

  &:last-child {
    margin-right: 0;
  }
`

const DayDetailLabel = styled.div`
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #999;
  text-transform: uppercase;
  margin-bottom: 5px;
  font-weight: 600;
  letter-spacing: 0.5px;

  i {
    margin-right: 5px;
  }
`

const DayDetailValue = styled.div`
  font-size: 14px;
  color: #333;
  font-weight: 500;
`

const ActionIcons = styled.div`
  display: flex;
  gap: 10px;
`

const RestrictionPeriods: React.FC = () => {
  const { t } = useTranslation()
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const displayAction = roleSlug === ADMIN_ROLE_SLUG || roleSlug === OPERATIVE_ROLE_SLUG
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
  const [drawer, setDrawerState] = useState({
    isVisible: false,
    id: null
  })
  const [selectedType, setSelectedType] = useState(null)

  const toggleRow = (id: number) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedRows(newExpanded)
  }

  const onSuccess = () => {
    notify(t('Successfully deleted restriction period'), 'success');
    fetchData();
  };

  const {
    isLoading: isBusyData,
    data: restrictionPeriods,
    refetch: fetchData
  } = DairyQuery.getRestrictionPeriods()

  useEffect(() => {
    fetchData()
  }, [t])

  const { isLoading: isDeleteLoading, mutate: deleteRestrictionPeriod } = DairyQuery.deleteRestrictionPeriod(
    onSuccess
  );

  async function onRestrictionPeriodDelete() {
    deleteRestrictionPeriod(selectedType?.id)
    setSelectedType(null)
  }

  const getDayName = (day: number) => {
    const days = [
      t('Monday'),
      t('Tuesday'),
      t('Wednesday'),
      t('Thursday'),
      t('Friday'),
      t('Saturday'),
      t('Sunday')
    ]
    return days[day - 1] || ''
  }

  const formatTime = (time: string) => {
    return time?.substring(0, 5) || ''
  }

  const renderDayDetails = (days: any[]) => {
    if (!days || days.length === 0) return null

    return (
      <ExpandedContent>
        {days.map((day) => (
          <DayDetailContainer key={day.id}>
            <DayDetailSection style={{ flex: 1 }}>
              <div>
                <DayDetailLabel>
                  <Icon name='calendar' />
                  {t('DAY')}
                </DayDetailLabel>
                <DayDetailValue>{day.label || getDayName(day.day)}</DayDetailValue>
              </div>
            </DayDetailSection>
            <DayDetailSection style={{ flex: 1 }}>
              <div>
                <DayDetailLabel>
                  <Icon name='clock' />
                  {t('TIME RANGE')}
                </DayDetailLabel>
                <DayDetailValue>
                  {formatTime(day.timeFrom)} → {formatTime(day.timeTo)}
                </DayDetailValue>
              </div>
            </DayDetailSection>
            <DayDetailSection style={{ flex: 1 }}>
              <div>
                <DayDetailLabel>
                  <Icon name='thermometer' />
                  {t('MAX TEMP')}
                </DayDetailLabel>
                <DayDetailValue>{day.maxTemp}°C</DayDetailValue>
              </div>
            </DayDetailSection>
          </DayDetailContainer>
        ))}
      </ExpandedContent>
    )
  }

  function renderTable() {
    const { filtered, rows } = restrictionPeriods ? restrictionPeriods : { filtered: 0, rows: [] };

    return (
      <StyledTable celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={1}></Table.HeaderCell>
            <Table.HeaderCell width={1}>{t('ID')}</Table.HeaderCell>
            <Table.HeaderCell width={4}>{t('Name')}</Table.HeaderCell>
            <Table.HeaderCell width={2}>{t('Status')}</Table.HeaderCell>
            <Table.HeaderCell width={2}>{t('Days')}</Table.HeaderCell>
            {displayAction && <Table.HeaderCell width={2}>{t('Actions')}</Table.HeaderCell>}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {rows.map((row) => (
            <React.Fragment key={row.id}>
              <ExpandableRow>
                <Table.Cell onClick={() => toggleRow(row.id)}>
                  <Icon
                    name={expandedRows.has(row.id) ? 'chevron down' : 'chevron right'}
                    style={{ cursor: 'pointer' }}
                  />
                </Table.Cell>
                <Table.Cell onClick={() => toggleRow(row.id)}>{row.id}</Table.Cell>
                <Table.Cell onClick={() => toggleRow(row.id)}>{row.name}</Table.Cell>
                <Table.Cell onClick={() => toggleRow(row.id)}>
                  <Label color={row.status === 1 ? 'green' : 'grey'} size='small'>
                    {row.status === 1 ? t('Active') : t('Inactive')}
                  </Label>
                </Table.Cell>
                <Table.Cell onClick={() => toggleRow(row.id)}>
                  {row.days?.length || 0} {row.days?.length === 1 ? t('day') : t('days')}
                </Table.Cell>
                {displayAction && (
                  <Table.Cell>
                    <ActionIcons>
                      <Popup
                        content={t('Edit')}
                        trigger={
                          <CustomIcon
                            size='large'
                            style={{ marginBottom: 2, cursor: 'pointer' }}
                            onClick={() => setDrawerState({ isVisible: true, id: row.id })}
                            name='pencil'
                          />
                        }
                      />
                      <Popup
                        content={t('Delete')}
                        trigger={
                          <CustomIcon
                            size='large'
                            style={{ marginBottom: 2, cursor: 'pointer' }}
                            onClick={() => setSelectedType(row)}
                            name='trash'
                          />
                        }
                      />
                    </ActionIcons>
                  </Table.Cell>
                )}
              </ExpandableRow>
              {expandedRows.has(row.id) && (
                <Table.Row>
                  <Table.Cell colSpan={displayAction ? 6 : 5} style={{ padding: 0 }}>
                    {renderDayDetails(row.days)}
                  </Table.Cell>
                </Table.Row>
              )}
            </React.Fragment>
          ))}
        </Table.Body>
      </StyledTable>
    )
  }

  function renderActions() {
    return <LayoutEl.PageHeaderActionButton
      onClick={() => setDrawerState({ isVisible: true, id: null })}
    >{t('Add new restriction period')}</LayoutEl.PageHeaderActionButton>
  }

  function renderEditCreateState() {
    return <CreateEditRestrictionPeriods
      isVisible={drawer.isVisible}
      idRestrictionPeriod={drawer.id}
      fetchData={fetchData}
      handleHiddenDrawer={() => setDrawerState({ isVisible: false, id: null })}
    />
  }

  function renderDeleteModal() {
    return <Dialog
      key='dialog-restriction-period-delete'
      textHeader={t(`Delete restriction period: ${selectedType?.name}`)}
      textClose={t('Close')}
      textContent={t('Are you sure that you want to delete {{period}} restriction period?', { period: selectedType?.name })}
      textSubmit={t('Delete restriction period')}
      handleClose={() => setSelectedType(null)}
      handleSubmit={onRestrictionPeriodDelete}
      type='abort'
      isDialogOpen={Boolean(selectedType)}
      isLoading={isDeleteLoading}
    />
  }

  return <Layout
    title={t('Restriction Periods')}
    isBusy={isBusyData}
    childContainerSize={'auto'}
    fontSizeHeader={'18px'}
    actions={displayAction && renderActions()}
    useSpinner
  >
    {selectedType && renderDeleteModal()}
    {renderEditCreateState()}
    {restrictionPeriods && renderTable()}
  </Layout>
}
export default RestrictionPeriods
